import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Box, Text, Textarea, Group, ActionIcon, Button } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import linkifyIt from 'linkify-it';
import { useDealRowRepository } from '@ayocore/erp/crm/client/rpc';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const linkify = (linkifyIt as any)();

/** Разбирает текст на фрагменты: обычный текст и кликабельные URL. */
function linkifyText(text: string): React.ReactNode[] {
  const matches = linkify.match(text);
  if (!matches) return [text];

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    result.push(
      <a
        key={match.index}
        href={match.url}
        target='_blank'
        rel='noopener noreferrer'
        style={{ color: 'var(--mantine-color-blue-6)' }}
      >
        {match.text}
      </a>
    );
    lastIndex = match.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

type DealRowNotesProps = {
  dealRowId: number;
  value: string | null;
  onSaved: (notes: string | null) => void;
};

export const DealRowNotes = React.memo(function DealRowNotes({
  dealRowId,
  value,
  onSaved,
}: DealRowNotesProps) {
  const repo = useDealRowRepository();
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevPropRef = useRef(value);

  // Синхронизация только когда внешний prop реально изменился (перезагрузка данных)
  if (value !== prevPropRef.current) {
    prevPropRef.current = value;
    if (!editing) {
      setLocalValue(value);
    }
  }

  const handleEdit = useCallback(() => {
    setDraft(localValue ?? '');
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [localValue]);

  const handleSave = useCallback(async () => {
    const trimmed = draft.trim() || null;
    if (trimmed === (localValue ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await repo.update(dealRowId, { notes: trimmed });
      setLocalValue(trimmed);
      onSaved(trimmed);
      setEditing(false);
    } catch {
      // оставляем в режиме редактирования при ошибке
    } finally {
      setSaving(false);
    }
  }, [draft, localValue, repo, dealRowId, onSaved]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setDraft(localValue ?? '');
  }, [localValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleCancel]
  );

  const displayContent = useMemo(() => (localValue ? linkifyText(localValue) : null), [localValue]);

  if (editing) {
    return (
      <Box py='xs' px='md'>
        <Text size='xs' fw={600} mb={4}>
          Примечание
        </Text>
        <div style={{ position: 'relative' }}>
          <Textarea
            ref={textareaRef}
            size='xs'
            autosize
            minRows={2}
            maxRows={8}
            value={draft}
            onChange={e => setDraft(e.currentTarget.value)}
            onBlur={e => {
              if (
                e.relatedTarget?.closest('[data-save-notes]') ||
                e.relatedTarget?.closest('button')
              )
                return;
              void handleSave();
            }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            placeholder='Введите заметку...'
          />
          <Group gap={6} mt={6} justify='flex-end'>
            <Button
              data-save-notes
              size='compact-xs'
              variant='filled'
              color='green'
              loading={saving}
              onClick={() => void handleSave()}
            >
              Сохранить
            </Button>
            <Button size='compact-xs' variant='default' disabled={saving} onClick={handleCancel}>
              Отменить
            </Button>
          </Group>
        </div>
      </Box>
    );
  }

  return (
    <Box py='xs' px='md'>
      <Group gap={6} align='baseline' wrap='nowrap'>
        <Text size='xs' fw={600} style={{ whiteSpace: 'nowrap' }}>
          Примечание
        </Text>
        {displayContent ? (
          <Text size='xs' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {displayContent}
          </Text>
        ) : (
          <Text size='xs' c='dimmed' style={{ cursor: 'pointer' }} onClick={handleEdit}>
            Нажмите чтобы добавить...
          </Text>
        )}
        <ActionIcon size='xs' variant='subtle' onClick={handleEdit} style={{ flexShrink: 0 }}>
          <IconPencil size={12} />
        </ActionIcon>
      </Group>
    </Box>
  );
});
